import { axiosAuth, axiosPublic } from 'Utils/axios'
import { UpdateUserProfileReq, User, BlacklistUserRes } from 'Types/user'
import { BASE_API_URL } from '@/constant/api'
import { AppResp } from 'Types/api'
import { Gender } from '@/types/types'
import { UploadScene, uploadFile } from '@/apis/upload'

export const getUser = async ({
  skipLoginModalOn401 = false,
}: {
  skipLoginModalOn401?: boolean
} = {}) => {
  const res = await axiosAuth.post<AppResp<User>>(
    `${BASE_API_URL}/user/info`,
    undefined,
    {
      skipLoginModalOn401,
    }
  )
  if (!res.data.data) {
    throw new Error('No user data')
  }
  return res.data.data
}

export const getPublicUserBySelf = async (user_id: string) => {
  const res = await axiosAuth.post<AppResp<User>>(
    `${BASE_API_URL}/user/get/public`,
    {
      user_id: user_id,
    }
  )
  return res.data.data
}

export const getPublicUser = async (user_id: string) => {
  const res = await axiosPublic.post<AppResp<User>>(
    `${BASE_API_URL}/user/get/public`,
    {
      user_id: user_id,
    }
  )
  return res.data.data
}

export const uploadUserAvatar = async (avatar: Blob) => {
  const uploadRes = await uploadFile({
    file: avatar,
    scene: UploadScene.USER_AVATAR,
  })

  const formData = new FormData()
  formData.append('object_key', uploadRes.key)
  const confirmRes = await axiosAuth.post<
    AppResp<{ key: string; url: string }>
  >(`${BASE_API_URL}/user/upload_avatar`, formData)

  return confirmRes.data.data
}

export const updateUserProfile = async (req: UpdateUserProfileReq) => {
  return axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/update_profile`,
    req
  )
}

export const updateUserAge = async (age: number) => {
  const res = await axiosAuth.post<AppResp<{ age: number }>>(
    `${BASE_API_URL}/user/age/update`,
    { age }
  )
  return res.data.data
}

// NSFW（Limitless）开关：独立接口，后端持久化
export const updateUserNsfw = async (nsfw: boolean) => {
  const res = await axiosAuth.post<AppResp<{ nsfw: boolean }>>(
    `${BASE_API_URL}/user/nsfw/update`,
    { nsfw }
  )
  if (res.data.code !== 0) {
    throw new Error(res.data.msg)
  }
  return res.data.data
}

export const updateUserRegisterProfile = async ({
  gender,
  nickname,
  basic_rules_completed,
  age = 0,
}: {
  gender: Gender
  nickname: string
  basic_rules_completed: boolean
  age?: number
}) => {
  return axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/register_profile`,
    {
      gender,
      nickname,
      basic_rules_completed,
      age,
    }
  )
}

// 根据用户设置的语言进行角色翻译
export const translateCharacterUrl = `${BASE_API_URL}/character/translate`
export const translateCharacter = async (characterId: string) => {
  const res = await axiosAuth.post<AppResp<string>>(translateCharacterUrl, {
    character_id: characterId,
  })

  if (!res.data.data) {
    throw new Error('No translate character data')
  }

  return res.data.data
}

export const translateStoryUrl = `${BASE_API_URL}/story/translate`
export const translateStory = async (storyId: string, languageCode: string) => {
  const res = await axiosAuth.post<AppResp<string>>(translateStoryUrl, {
    story_id: storyId,
    language_code: languageCode,
  })

  if (!res.data.data) {
    throw new Error('No translate story data')
  }
  return res.data.data
}

// Set user language preference
export const setUserLanguage = async (languageCode: string): Promise<void> => {
  try {
    await axiosAuth.post<AppResp<undefined>>(
      `${BASE_API_URL}/user/set_language`,
      { language_code: languageCode }
    )
  } catch (error) {
    console.error('Error setting user language:', error)
    throw error
  }
}

export type DeleteUserReq = {
  reason_type: number
  primary_reason?: string
  secondary_reason_type?: number
  secondary_reason?: string
  custom_reason?: string
  suggestion?: string
  platform?: string
  device_info?: string
  is_open_to_interview?: boolean
  interview_email?: string
}

export const deleteUserKey = '/user/delete'
export const deleteUser = async (data: DeleteUserReq) => {
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}${deleteUserKey}`,
    data,
    {
      headers: appVersion ? { 'X-App-Version': appVersion } : undefined,
    }
  )

  if (res.data.code !== 0) {
    throw new Error(res.data.msg)
  }

  return res.data.data
}

export const getRecentChattedCharacter = async (user_id: string) => {
  const res = await axiosAuth.post<AppResp<{ character_id: string }>>(
    `${BASE_API_URL}/user/chatted/character/recent`,
    {
      user_id,
    }
  )
  return res.data.data?.character_id
}

// 检查用户是否为新用户
export const getIsNewUser = async () => {
  const res = await axiosAuth.post<AppResp<{ is_new: boolean }>>(
    `${BASE_API_URL}/user/is_new`
  )
  if (!res.data.data) {
    throw new Error('No data')
  }
  return res.data.data
}

export const is_paid_user = async () => {
  const res = await axiosAuth.post<AppResp<{ has_paid: boolean }>>(
    `${BASE_API_URL}/subscription/payment/has_paid`
  )
  if (!res.data.data) {
    throw new Error('No data')
  }
  return res.data.data.has_paid
}

// blacklist apis
export const addUserToBlacklist = async (
  blocked_user_id: string,
  block_type: 1 | 2
) => {
  const res = await axiosAuth.post<AppResp<null>>(
    `${BASE_API_URL}/user/blacklist/add`,
    { blocked_user_id, block_type }
  )
  return res.data.data
}

export const removeUserFromBlacklist = async (
  blocked_user_id: string,
  block_type: 1 | 2
) => {
  const res = await axiosAuth.post<AppResp<null>>(
    `${BASE_API_URL}/user/blacklist/remove`,
    { blocked_user_id, block_type }
  )
  return res.data.data
}

export const getBlacklistedUserList = async (
  page: number,
  page_size: number,
  type?: 1 | 2
) => {
  const res = await axiosAuth.post<
    AppResp<{ total: number; users: BlacklistUserRes[] }>
  >(`${BASE_API_URL}/user/blacklist/list`, {
    page: page,
    page_size: page_size,
    block_type: type,
  })
  return res.data.data
}

export const getBlacklistedUserListKey =
  (page_size: number, type?: 1 | 2) =>
  (pageIndex: number, prev: { users: BlacklistUserRes[] } | null) => {
    if (prev && prev.users.length < page_size) return null
    return {
      endpoint: '/user/blacklist/list',
      req: {
        page: pageIndex + 1,
        page_size: page_size,
        block_type: type,
      },
    }
  }

export const getUserCountry = async () => {
  const res = await axiosPublic.post<AppResp<{ country: string }>>(
    `${BASE_API_URL}/user/country`
  )
  if (!res.data.data) {
    throw new Error('No data')
  }
  return res.data.data.country
}
